import React, { useCallback } from 'react';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import FieldPasswordInput from 'Components/FormFields/FieldPasswordInput';
import FieldTextInput from 'Components/FormFields/FieldTextInput';
import UIButton from 'Components/UIComponents/UIButton';
import UISpinner from 'Components/UIComponents/UISpinner';
import arrayMutators from 'final-form-arrays';
import { AuthData } from 'Interfaces/Auth';
import { UnauthorizedRoutePaths } from 'Interfaces/RoutePaths';
import { toLowerCaseAndRemoveEmptyCharacters } from 'Utils/formatters';
import { composeValidators } from 'Utils/functions';
import { email, required } from 'Utils/validation';

import GoogleLoginForm from '../GoogleLoginForm';
import { AuthFormProps } from '../interfaces/AuthFormProps';

interface LoginFormProps extends AuthFormProps {
  onSignUpClick?: () => void;
  formClassName?: string;
}

function LoginForm({
  onSubmit,
  isLoading,
  formClassName,
  onSignUpClick,
}: LoginFormProps) {
  const navigate = useNavigate();

  const navigateToSignUp = useCallback(() => navigate(UnauthorizedRoutePaths.SIGN_UP), [
    navigate,
  ]);

  return (
    <Form
      onSubmit={onSubmit}
      mutators={{ ...arrayMutators }}
      render={({
        handleSubmit,
        submitting,
        pristine,
        hasValidationErrors,
      }: FormRenderProps<AuthData>) => {
        return (
          <form
            className={classNames(
              'auth__form auth__form--login auth__form--border',
              formClassName,
            )}
          >
            <Field
              name="email"
              label="Email Address"
              component={FieldTextInput}
              parse={toLowerCaseAndRemoveEmptyCharacters}
              placeholder="username@gmail.com"
              validate={composeValidators<string>(required, email)}
            />
            <div className="auth__field auth__field--password">
              <Field
                name="password"
                label="Password"
                type="password"
                component={FieldPasswordInput}
                validate={required}
                placeholder="Your password"
              />
              <div className="auth__forgot">
                <div
                  className="auth__link"
                  onClick={() => {
                    navigate(UnauthorizedRoutePaths.RESET);
                  }}
                >
                  Forgot password?
                </div>
              </div>
            </div>
            <div className="auth__submitButton">
              {isLoading ? (
                <UISpinner
                  width={40}
                  height={40}
                  wrapperClassName="spinner--main__wrapper"
                />
              ) : (
                <UIButton
                  priority="primary"
                  title="Login"
                  handleClick={handleSubmit}
                  type="submit"
                  disabled={pristine || submitting || hasValidationErrors}
                  isLoading={submitting}
                />
              )}
            </div>
            <div className="common__or">OR</div>
            <div className="auth__googleButton-wrapper">
              <GoogleLoginForm onSubmit={onSubmit} width={300} text={'signin_with'} />
            </div>
            <hr className="auth__hr" />
            <div className="auth__check-account">
              Don’t have an account?&nbsp;
              <div className="auth__link" onClick={onSignUpClick || navigateToSignUp}>
                Sign Up
              </div>
            </div>
          </form>
        );
      }}
    />
  );
}

export default LoginForm;
